import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import { createReduxHistoryContext } from 'redux-first-history';
import { createBrowserHistory } from 'history';
import { createReduxEnhancer } from '@sentry/react';
import { listenerMiddleware } from '@client/listeners/lib';
import reducers from '../slices';
import { Pool } from './pool';
import { filterItems } from './filter.transform';
import { errorMiddleware } from './error.middleware';

let preloadedState = {};

/* eslint-disable no-underscore-dangle */
if (window && window.__PRELOADED_STATE__) {
  preloadedState = window.__PRELOADED_STATE__;
  delete window.__PRELOADED_STATE__;
}

const {
  createReduxHistory,
  routerMiddleware,
  routerReducer,
} = createReduxHistoryContext({ history: createBrowserHistory() });

const sentryReduxEnhancer = createReduxEnhancer();

const rootReducer = combineReducers({
  router: routerReducer,
  ...reducers,
});

const persistConfig = {
  key: 'root',
  storage,
  version: 4,
  stateReconciler: autoMergeLevel2,
  whitelist: ['categories', 'session', 'acts', 'venues', 'profiles'],
  transforms: [filterItems],
};

const persistedReducer = persistReducer<any, any>(persistConfig, rootReducer) as typeof rootReducer;
export const backupReducer = persistReducer({ ...persistConfig, whitelist: [], version: 2 }, rootReducer);

const configuredStore = configureStore({
  preloadedState,
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV === 'development',
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false, thunk: false }).prepend([
    errorMiddleware,
    routerMiddleware,
    listenerMiddleware.middleware,
  ]),
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat([
    sentryReduxEnhancer,
  ]),
});

const store = configuredStore;
export const persistor = persistStore(store);

export const getStore = () => store;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const defaultPool: Pool = new Pool(store.dispatch);

export const history = createReduxHistory(store);
